import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cachedPatientService, cachedInsuranceService } from '../services/cachedApiService';
import { Patient} from '../types';
import Layout from '../components/Layout';
import '../styles/PatientList.css';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';

// Define a type for our request items
interface RequestItem {
  id: number;
  patientId: string;
  patientName: string;
  requestType: 'Procedure' | 'Prescription';
  requestName: string;
  paRequired: boolean;
  hasEPA: boolean;
}

const RequestList: React.FC = () => {
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<RequestItem[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        
        let patientData: Patient[] = [];
        
        // If user is a provider, fetch only assigned patients
        if (user?.role === 'provider') {
          const assignedPids = await cachedPatientService.getAssignedPatients(user.username);
          const allPatients = await cachedPatientService.getAllPatients();
          patientData = allPatients.filter(patient => assignedPids.includes(patient.pid));
        } else if (user?.role === 'superuser') {
          // If user is a superuser, fetch all patients
          patientData = await cachedPatientService.getAllPatients();
        }
        
        // Fetch all insurance data to check for ePA
        const insurances = await cachedInsuranceService.getAllInsurance();
        
        // Create a map of patient IDs to ePA status
        const patientEPAMap = new Map<string, boolean>();
        insurances.forEach(ins => {
          if (ins.ePA) {
            patientEPAMap.set(ins.patient, true);
          }
        });
        
        // Fetch detailed information for each patient to get procedures and prescriptions
        const allRequests: RequestItem[] = [];
        
        for (const patient of patientData) {
          try {
            const patientDetail = await cachedPatientService.getPatientDetail(patient.pid);
            const hasEPA = patientEPAMap.has(patient.pid);
            
            // Add procedures that require PA
            if (patientDetail.encounters) {
              patientDetail.encounters.forEach(encounter => {
                encounter.procedures.forEach(procedure => {
                  if (procedure.PA_required) {
                    allRequests.push({
                      id: procedure.id,
                      patientId: patient.pid,
                      patientName: `${patient.fname} ${patient.lname}`,
                      requestType: 'Procedure',
                      requestName: procedure.procedure_name || procedure.procedure_code,
                      paRequired: procedure.PA_required,
                      hasEPA: hasEPA
                    });
                  }
                });
              });
            }
            
            // Add prescriptions that require PA
            if (patientDetail.prescriptions) {
              patientDetail.prescriptions.forEach(prescription => {
                if (prescription.PA_required) {
                  allRequests.push({
                    id: prescription.id,
                    patientId: patient.pid,
                    patientName: `${patient.fname} ${patient.lname}`,
                    requestType: 'Prescription',
                    requestName: prescription.drug_name,
                    paRequired: prescription.PA_required,
                    hasEPA: hasEPA
                  });
                }
              });
            }
          } catch (err) {
            console.error(`Error fetching details for patient ${patient.pid}:`, err);
          }
        }
        
        setRequests(allRequests);
        setFilteredRequests(allRequests);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch requests');
        setLoading(false);
      }
    };

    fetchRequests();
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading requests...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error">
          {error}
          <button
            onClick={() => {
              invalidate.patients();
              invalidate.insurance();
              window.location.reload();
            }}
            className="btn btn-primary mt-3"
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
  <Layout>
    <div className="container">
      {/* <div className="back-btn">
        <Link to="/dashboard"><button>⬅️ Back to Dashboard</button></Link>
      </div> */}
      <h2 className="heading">Prior Authorization Requests</h2>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by patient name, medication, or request type..."
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              if (value.trim() === '') {
                setFilteredRequests(requests);
              } else {
                setFilteredRequests(
                  requests.filter(request =>
                    request.patientName.toLowerCase().includes(value.toLowerCase()) ||
                    request.requestName.toLowerCase().includes(value.toLowerCase()) ||
                    request.requestType.toLowerCase().includes(value.toLowerCase())
                  )
                );
              }
            }}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={() => {
                setSearchTerm('');
                setFilteredRequests(requests);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
        <div className="search-results-count">
          {filteredRequests.length} {filteredRequests.length === 1 ? 'request' : 'requests'} found
        </div>
      </div>

      {filteredRequests.length > 0 ? (
        <ul className="request-list">
          {filteredRequests.map(request => (
            <li key={`${request.requestType}-${request.id}`} className="list-item">
              <div className="item-content">
                <Link to={`/patients/${request.patientId}`} className="patient-link">
                  <span className="patient-label">{request.patientName}</span>
                  <span className="patient-id">({request.patientId})</span>
                </Link>
                
                <div className="info-container">
                  <div className="info-left">
                    <div className="info-row">
                      <strong>Type:</strong>
                      <span>{request.requestType === 'Procedure' ?
                        'Procedure' :
                        'Prescription'}</span>
                    </div>
                    
                    <div className="info-row">
                      <strong>Name:</strong>
                      <span title={request.requestName}>{request.requestName.length > 20 ? request.requestName.substring(0, 20) + '...' : request.requestName}</span>
                    </div>
                  </div>
                  
                  <div className="info-right">
                    <div className="info-row">
                      <strong>PA:</strong>
                      <span className={request.paRequired ? 'yes-check' : 'no-x'}>
                        {request.paRequired ? 'Yes' : 'No'}
                      </span>
                    </div>
                    
                    <div className="info-row">
                      <strong>ePA:</strong>
                      <span className={`epastatus ${request.hasEPA ? 'available' : 'not-available'}`}>
                        {request.hasEPA ? 'Available' : 'Not Available'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {request.paRequired && (
                <Link to={`/raise-pa/${request.patientId}`} className="btn-raise-link">
                  <span>Raise PA</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="empty">
          {requests.length > 0
            ? `No requests found matching "${searchTerm}"`
            : "No requests requiring prior authorization found."}
          
          {requests.length > 0 && searchTerm && (
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginTop: '10px' }}
              onClick={() => {
                setSearchTerm('');
                setFilteredRequests(requests);
              }}
            >
              Clear search
            </button>
          )}
        </div>
      )}

      
    </div>
  </Layout>
  );
};

export default RequestList;